import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "./ui/PageHeader";
import { ArrowUpRight } from "./ui/Icons";

import bubuyaImage from "../image/bubuya.jpeg";
import diegoImage from "../image/Diego.jpg";
import nancyImage from "../image/nancy.jpeg";
import jarryImage from "../image/jarry.jpg";
import elogeImage from "../image/eloge.jpg";
import neemaImage from "../image/Neema.jpeg";

const teamMembers = [
  {
    name: "Elie Bubuya",
    role: "CEO & Machine Learning Engineer",
    image: bubuyaImage,
    description:
      "Elie Bubuya is the CEO and co-founder of Hyphen Tech, a machine learning engineer passionate about using technology to solve agricultural challenges. He studied machine learning in a workshop in Benin, with a strong focus on integrating AI into agriculture.",
  },
  {
    name: "Dieudonné Abanabimana",
    role: "CTO & Software Engineer",
    image: diegoImage,
    description:
      "Abanabimana Dieudonne is the CTO and co-founder of Hyphen Tech. He is a passionate software engineer with a deep love for informatics. His experience and enthusiasm for technology drive his commitment to developing powerful software solutions.",
  },
  {
    name: "Nancy Kwezi",
    role: "Agronomist",
    image: nancyImage,
    description:
      "Kwezi Nancy is an agronomist with a keen interest in technology. She has completed an internship focused on potatoes. Her expertise in agronomy ensures that the solutions developed are highly relevant for farmers and agricultural stakeholders.",
  },
  {
    name: "Don Divin Rodrigue NEEMA-MATEMERE",
    role: "Agronomist & Ecologist",
    image: neemaImage,
    description:
      "Don Divin Rodrigue NEEMA-MATEMERE is a Burundian agronomist with over seven years of experience in sustainable agriculture and ecology. He specializes in crop and animal production, health diagnostics, and optimizing agricultural techniques.",
  },
  {
    name: "Nigaba Jarry Hugue",
    role: "Economist & Market Analyst",
    image: jarryImage,
    description:
      "Jarry Hugue Nigaba provides economic insights and market analysis for AgriHyphen AI. His role is to conduct market research, ensuring the platform meets farmers' needs and aligns with current economic trends.",
  },
  {
    name: "Abikunda Eloge",
    role: "Product & Marketing Manager",
    image: elogeImage,
    description:
      "Eloge Abikunda manages product development, marketing strategy, and distribution. He ensures that the AgriHyphen AI platform is effectively positioned in the market and reaches farmers and cooperatives.",
  },
];

/**
 * Bios are always visible. The previous grid hid every bio behind a hover
 * overlay, which meant touch users could never read any of them.
 */
const Portrait = ({ member, index, featured }) => (
  <article>
    <div className="overflow-hidden bg-agri-paperDeep">
      <img
        src={member.image}
        alt={`Portrait of ${member.name}`}
        loading="lazy"
        className={`w-full object-cover object-top ${
          featured ? "aspect-[4/5]" : "aspect-square"
        }`}
      />
    </div>

    <div className="mt-5 border-t border-agri-rule pt-4">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[0.625rem] font-medium tabular-nums text-agri-clay">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h2
          className={`font-display font-semibold leading-tight tracking-[-0.018em] text-agri-ink ${
            featured ? "text-[1.375rem]" : "text-[1.0625rem]"
          }`}
        >
          {member.name}
        </h2>
      </div>
      <p className="mt-1.5 pl-[1.6rem] font-mono text-[0.6875rem] uppercase leading-relaxed tracking-[0.1em] text-agri-ink40">
        {member.role}
      </p>
      <p
        className={`mt-4 pl-[1.6rem] leading-[1.7] text-agri-ink80 ${
          featured ? "text-[0.9375rem]" : "text-[0.875rem]"
        }`}
      >
        {member.description}
      </p>
    </div>
  </article>
);

const Team = () => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const featured = teamMembers.slice(0, 2);
  const rest = teamMembers.slice(2);

  return (
    <main className="bg-agri-paper">
      <PageHeader
        kicker="Who we are"
        title={t("Meet Our Team")}
        lede={t("The passionate individuals behind AgriHyphen AI")}
      />

      <div className="container-x section-tight">
        {/* Founders carry more weight than the rest of the masthead */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:gap-x-12">
          {featured.map((m, i) => (
            <Portrait key={m.name} member={m} index={i} featured />
          ))}
        </div>

        {/* Four across, so the row fills evenly rather than orphaning one */}
        <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-12 border-t border-agri-rule pt-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-10">
          {rest.map((m, i) => (
            <Portrait key={m.name} member={m} index={i + 2} />
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 items-end gap-6 border-t border-agri-rule pt-10 sm:grid-cols-12">
          <div className="sm:col-span-7">
            <p className="kicker-rule">Join us</p>
            <p className="mt-4 max-w-measure font-display text-[1.5rem] font-semibold leading-tight tracking-[-0.02em] text-agri-ink">
              Interested in joining our mission?
            </p>
            <p className="mt-2 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
              We&rsquo;re always looking for agronomists, engineers and field
              partners who want to see this reach more farms.
            </p>
          </div>
          <div className="sm:col-span-5 sm:flex sm:justify-end">
            <a href="mailto:contact@hyphentech.africa" className="btn-outline">
              Get in touch
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Team;
